import { useCallback, useEffect } from "react";
import type { UUID } from "node:crypto";
import type { PreOrder } from "@/app/api/types/preOrder";
import { useUpdatePreOrder } from "@/query/hooks/usePreOrders";
import { isPreOrderModifiable } from "../services/recapService";
import { CommentPopover } from "./ui/CommentPopover";
import { PendingFieldOverlay } from "./ui/PendingFieldOverlay";
import { usePendingHeaderFields } from "../hooks/usePendingHeaderFields";
import { useProposalHeaderHandler } from "../hooks/useProposalHeaderHandler";
import { usePreOrderLockOptional } from "../contexts/PreOrderLockContext";
import { isProposalPreOrder } from "../types/proposal.types";
import { usePreOrderFeatureFlags } from "../hooks/usePreOrderFeatureFlags";
import { useRecapModeOptional } from "../contexts/RecapContext";
import {
  recordPendingHeaderWrite,
  clearPendingHeaderWrite,
} from "../services/pending-header-writes";
import { track } from "@/lib/analytics";

interface PreOrderCommentBlockProps {
  preOrder: PreOrder;
}

/**
 * Standalone pre-order general comment editor — mounts the same
 * <CommentPopover> the legacy <PreOrderHeader> wires up, with the same
 * dispatch + pending-field plumbing, but as a separate block the focus
 * page can drop above the table card. Locked / proposal / vivo behaviour
 * matches the legacy header so users see identical affordances regardless
 * of which mount point is on screen.
 */
export function PreOrderCommentBlock({ preOrder }: PreOrderCommentBlockProps) {
  const { isVivoMode } = usePreOrderFeatureFlags();
  const updatePreOrderMutation = useUpdatePreOrder();
  const proposalHeader = useProposalHeaderHandler({ preOrder });
  const pendingFields = usePendingHeaderFields();
  const lock = usePreOrderLockOptional();
  const mode = useRecapModeOptional() ?? undefined;

  const isProposal = isProposalPreOrder(preOrder);
  // Only PENDING / AI_PROPOSAL pre-orders accept comment edits.
  const isModifiable = isPreOrderModifiable(preOrder);

  const dispatchHeaderUpdate = useCallback(
    (data: Record<string, unknown>) => {
      // Track every top-level key being written. `pre_order_metadata`
      // updates are tracked under the synthetic "comment" key so the
      // wrapper around CommentPopover (which owns the m3_comments
      // editor) lights up too. Mirrors the same handler in
      // <PreOrderHeader> — kept verbatim so behaviour stays in lockstep
      // (and so a future hook extraction can swap both call sites at
      // once).
      const keys: string[] = [];
      const expectedValues: Record<string, unknown> = {};
      for (const rawKey of Object.keys(data)) {
        if (rawKey === "pre_order_metadata") {
          keys.push("comment");
        } else {
          keys.push(rawKey);
          expectedValues[rawKey] = data[rawKey];
        }
      }
      // Product analytics: one event per committed header field, fired only
      // once the write succeeds (optimistic apply lands / proposal dispatch
      // resolves) — never on a rejected edit, matching OrderDetailsSection.
      // Iterate the mapped `keys` (not raw `Object.keys(data)`) so an
      // `pre_order_metadata` m3_comments save reports `field: "comment"` like
      // the general comment save does — otherwise one engagement signal splits
      // across two field names. `field` is the key name only, never the text.
      const fireFieldEvents = () => {
        for (const field of keys) {
          track("pre_order_header_changed", {
            pre_order_id: preOrder.id,
            field,
            surface: "po_detail",
            mode,
          });
        }
      };
      if (proposalHeader) {
        pendingFields.track(
          keys,
          (onSettled) => {
            Promise.resolve(proposalHeader.dispatch(data))
              .then(() => fireFieldEvents())
              .finally(onSettled);
          },
          expectedValues,
        );
        return;
      }
      // Record the intended header value so an immediately-following confirm
      // can fold it into the export push-batch instead of racing this
      // change-queue write (STK-991). Recorded before the mutate so it is
      // present by the time the confirm gesture (which triggered this blur)
      // reads it.
      recordPendingHeaderWrite(preOrder.id, data);
      pendingFields.track(
        keys,
        (onSettled) => {
          updatePreOrderMutation.mutate(
            { id: preOrder.id as UUID, data },
            { onSuccess: () => fireFieldEvents(), onSettled },
          );
        },
        expectedValues,
      );
    },
    [proposalHeader, updatePreOrderMutation, preOrder.id, pendingFields, mode],
  );

  // Bound the module-level registry: a header value recorded but never
  // confirmed (edit-then-park, or a repeatedly-failing confirm) is dropped when
  // this order's editor unmounts or the focus row changes, so a stale value can
  // never be folded into a later confirm (STK-991). The change-queue write
  // remains the durable backstop for anything actually edited.
  useEffect(() => () => clearPendingHeaderWrite(preOrder.id), [preOrder.id]);

  const disabled = (!isModifiable && !isProposal) || lock.isLocked;

  return (
    <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/30">
      <PendingFieldOverlay
        isPending={pendingFields.isPending("comment", preOrder.comment ?? "")}
        className="block w-full"
      >
        <CommentPopover
          value={preOrder.comment || ""}
          onChange={(value) => dispatchHeaderUpdate({ comment: value })}
          disabled={disabled}
          isVivoMode={isVivoMode}
          flowMetadata={preOrder.pre_order_metadata}
          onM3CommentsChange={(m3Comments) =>
            dispatchHeaderUpdate({
              pre_order_metadata: {
                ...(preOrder.pre_order_metadata || {}),
                m3_comments: m3Comments,
              },
            })
          }
        />
      </PendingFieldOverlay>
    </div>
  );
}
